import { useMemo } from "react";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { FormattedRpServiceConfiguration, FormattedRpServiceConfigurationServiceOffering } from "@idw-types/data/rp-service-configuration";
import { ApAssertionsTabContainer } from "./ap-assertions-tab-container/ap-assertions-tab-container";
import { ThemingSubTabContainer } from "../../../common/theming-sub-tab-container/theming-sub-tab-container";
import { AssertionAttribute } from "@idw-types/data/reference-data/generic";
import { formatScoreModifiers } from "@formatters/rp-services/rp-service-configuration-score-formatter";
import { isAssertionScoreEnabled, isTrustScoreEnabled } from "@utils/scores/score-util";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";

type VerificationServiceDetailsAssertionsTabProps = {
    isDisabled: boolean;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    assertionStatusMaps: Array<ApAssertionStatusMap>;
    assertionScoreServiceOfferingIDs: Array<string>;
    trustScoreServiceOfferingIDs: Array<string>;
    assertionAttributes: Array<AssertionAttribute>;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
};

export const VerificationServiceDetailsAssertionsTab = (props: VerificationServiceDetailsAssertionsTabProps) => {
    const {
        isDisabled,
        rpServiceConfigurationState,
        assertionStatusMaps,
        assertionScoreServiceOfferingIDs,
        trustScoreServiceOfferingIDs,
        assertionAttributes,
        setRpServiceConfigurationState
    } = props;

    const setRpServiceConfigurationServiceOffering = (serviceOffering: FormattedRpServiceConfigurationServiceOffering) => {
        const newServiceOfferings = rpServiceConfigurationState.serviceOfferings.map(so => so.uuid === serviceOffering.uuid
            ? serviceOffering
            : so
        );

        const newRpServiceConfigurationState = { ...rpServiceConfigurationState, serviceOfferings: newServiceOfferings };

        if (isAssertionScoreEnabled(newServiceOfferings, assertionScoreServiceOfferingIDs)) {
            newRpServiceConfigurationState.assertionScoreModifiers = formatScoreModifiers(rpServiceConfigurationState.assertionScoreModifiers, newServiceOfferings,
                assertionStatusMaps
            );
        }

        if (isTrustScoreEnabled(newServiceOfferings, trustScoreServiceOfferingIDs)) {
            newRpServiceConfigurationState.trustScoreModifiers = formatScoreModifiers(rpServiceConfigurationState.trustScoreModifiers, newServiceOfferings,
                assertionStatusMaps
            );
        }

        setRpServiceConfigurationState(newRpServiceConfigurationState);
    };

    const selectedServiceOfferings = useMemo(() => {
        return rpServiceConfigurationState.serviceOfferings
            .filter(so =>
                so.addedToService &&
                so.status === EntityStatusTypes.ACTIVE &&
                so.serviceOfferingStatus === EntityStatusTypes.ACTIVE &&
                so.assertions.length > 0
            );

    }, [rpServiceConfigurationState.serviceOfferings]);

    return (
        <ThemingSubTabContainer>
            <Tabs id="assertions" unmountOnExit={true}>
                {selectedServiceOfferings.map(so => (
                    <Tab key={`${so.name}-ap-assertions-tab-container`} eventKey={`${so.name}-ap-assertions`} title={so.name}>
                        <ApAssertionsTabContainer
                            isDisabled={isDisabled}
                            rpServiceConfigurationServiceOffering={so}
                            setRpServiceConfigurationServiceOffering={setRpServiceConfigurationServiceOffering}
                            rpServiceConfigurationState={rpServiceConfigurationState}
                            assertionAttributes={assertionAttributes}
                        />
                    </Tab>
                ))}
            </Tabs>
        </ThemingSubTabContainer>
    );
};